// client/src/components/pages/Settings/TelemetryDevices.tsx (converted from .jsx, ST-00240)
//
// LexiStar (Satellite Tracking And Reporting) — GPS device registry. Settings → LexiStar →
// Devices since ST-00240 (was under Monitoring); the address is unchanged.
// Register tracker devices (IMEI -> tenant), classify them as Fleet vehicles
// (trucks) or Loaders, assign vehicle registrations, and onboard unknown
// devices that have knocked on the ingest endpoint.
//
// Permissions: page requires telemetry.view (see pagePermissions.ts);
// registration / editing require telemetry.devices.manage. The unknown-device list (IMEIs
// that knocked unregistered: no tenant yet, so cross-tenant) is the global administrators'
// only — the server answers it for them alone (ST-00258: requested, and its panel and tile
// shown, only for global-admin; any failure of that list leaves the device list standing).

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { FormEvent } from 'react';
import type { AxiosError, AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Satellite,
  Radio,
  Truck,
  Forklift,
  CheckCircle2,
  AlertTriangle,
  Edit3,
  X,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  Ban,
} from 'lucide-react';
import api from '../../../services/api';
import ThemedSelect from '../../common/ThemedSelect';
import { useAuth } from '../../../context/AuthContext';
import { formatRelativeTime, formatAppDateTime } from '../../../utils/dateUtils';
import type {
  RegisterTelemetryDeviceBody,
  TelemetryDevice,
  TelemetryDevicesResponse,
  UnknownTelemetryDevice,
  UnknownTelemetryDevicesResponse,
  UpdateTelemetryDeviceBody,
} from '../../../types/clientPages/telemetryDevices';

const pageStyles = `
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.96) translateY(4px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  .anim-stagger { animation: slideUp 0.35s ease-out both; }
`;

const inputClasses =
  'w-full px-3.5 py-2.5 bg-surface border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition-all outline-none';

const IMEI_RE = /^\d{8,17}$/;

interface RegisterForm {
  imei: string;
  label: string;
  model: string;
  vehicleClass: string;
  vehicleRegNo: string;
}

interface EditForm {
  label: string;
  status: string;
  vehicleClass: string;
  vehicleRegNo: string;
}

/** The server's `{ error }` message, else the request's own. */
const errorText = (err: unknown): string => {
  const e = err as AxiosError<{ error?: string }> | undefined;
  return e?.response?.data?.error || e?.message || 'Something went wrong';
};

const EMPTY_REGISTER_FORM: RegisterForm = {
  imei: '',
  label: '',
  model: '',
  vehicleClass: 'truck',
  vehicleRegNo: '',
};

// --- Class badge (Fleet = truck / Loader) ---
const ClassBadge = ({ vehicleClass }: { vehicleClass: string }) => {
  const isFleet = vehicleClass === 'truck';
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
        isFleet ? 'bg-sky-50 text-sky-700' : 'bg-amber-50 text-amber-700'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isFleet ? 'bg-sky-500' : 'bg-amber-500'}`} />
      {isFleet ? 'Fleet' : 'Loader'}
    </span>
  );
};

// --- Status badge ---
const StatusBadge = ({ status }: { status: string }) =>
  status === 'active' ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-600">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-500">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
      Disabled
    </span>
  );

// --- Relative timestamp cell ---
const TimeCell = ({ value }: { value: string | null }) => {
  if (!value) return <span className="text-xs text-slate-300 italic">Never</span>;
  return (
    <span className="text-sm text-slate-600" title={formatAppDateTime(value)}>
      {formatRelativeTime(value)}
    </span>
  );
};

// --- Edit device modal ---
interface EditDeviceModalProps {
  isOpen: boolean;
  device: TelemetryDevice | null;
  onClose: () => void;
  onSaved: () => void;
}

const EditDeviceModal = ({ isOpen, device, onClose, onSaved }: EditDeviceModalProps) => {
  const [form, setForm] = useState<EditForm>({
    label: '',
    status: 'active',
    vehicleClass: 'truck',
    vehicleRegNo: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && device) {
      setForm({
        label: device.label || '',
        status: device.status || 'active',
        vehicleClass: device.vehicle_class || 'truck',
        vehicleRegNo: device.vehicle_reg_no || '',
      });
      setFormError(null);
    }
  }, [isOpen, device]);

  if (!isOpen || !device) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const body: UpdateTelemetryDeviceBody = {
        label: form.label.trim(),
        status: form.status,
        vehicleClass: form.vehicleClass,
        vehicleRegNo: form.vehicleRegNo.trim(), // empty string unassigns
      };
      await api.telemetry.updateDevice(device.device_id, { ...body });
      onSaved();
      onClose();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Edit Device</h3>
            <p className="text-sm text-slate-500 mt-0.5 font-mono tracking-wider">{device.imei}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                {formError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Label</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className={inputClasses}
                placeholder="e.g. FMC003 test unit"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Class</label>
                <ThemedSelect
                  value={form.vehicleClass}
                  onChange={(e) => setForm({ ...form, vehicleClass: String(e.target.value) })}
                  className={inputClasses}
                >
                  <option value="truck">Fleet vehicle (truck)</option>
                  <option value="loader">Loader</option>
                </ThemedSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                <ThemedSelect
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: String(e.target.value) })}
                  className={inputClasses}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </ThemedSelect>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Vehicle Reg No
              </label>
              <input
                type="text"
                value={form.vehicleRegNo}
                onChange={(e) => setForm({ ...form, vehicleRegNo: e.target.value })}
                className={inputClasses}
                placeholder="e.g. ND 123-456"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Leave blank to unassign the device from its vehicle.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Update Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export default function TelemetryDevices() {
  const navigate = useNavigate();
  const { hasPermission, hasAppRole } = useAuth();
  const canManage = hasPermission('telemetry.devices.manage');
  // ST-00258: GET /telemetry/devices/unknown/list is global-admin only (routes/telemetryDevices.ts).
  const canSeeUnknown = canManage && hasAppRole('global-admin');

  const [devices, setDevices] = useState<TelemetryDevice[]>([]);
  const [unknownDevices, setUnknownDevices] = useState<UnknownTelemetryDevice[]>([]);
  // The unknown list's own outcome: shown, refused (403: hide it), or failed (say so in its panel).
  const [unknownState, setUnknownState] = useState<'shown' | 'refused' | 'failed'>('shown');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [registerForm, setRegisterForm] = useState<RegisterForm>(EMPTY_REGISTER_FORM);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; device: TelemetryDevice | null }>({
    open: false,
    device: null,
  });
  const registerCardRef = useRef<HTMLDivElement | null>(null);
  const imeiInputRef = useRef<HTMLInputElement | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // The unknown list never takes the device list down with it (ST-00258).
      const unknownRequest: Promise<AxiosResponse | 'refused' | 'failed' | null> = canSeeUnknown
        ? api.telemetry
            .getUnknownDevices()
            .catch((err: unknown) =>
              (err as AxiosError | undefined)?.response?.status === 403 ? 'refused' : 'failed'
            )
        : Promise.resolve(null);
      const [devicesRes, unknownRes] = await Promise.all([api.telemetry.getDevices(), unknownRequest]);
      setDevices((devicesRes?.data as TelemetryDevicesResponse | undefined)?.devices || []);
      if (unknownRes === 'refused' || unknownRes === 'failed') {
        setUnknownState(unknownRes);
        setUnknownDevices([]);
      } else {
        setUnknownState('shown');
        setUnknownDevices(
          (unknownRes?.data as UnknownTelemetryDevicesResponse | undefined)?.unknownDevices || []
        );
      }
    } catch (err) {
      if ((err as AxiosError | undefined)?.response?.status === 403)
        setError('You do not have permission to view LexiStar devices.');
      else setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [canSeeUnknown]);

  const showUnknown = canSeeUnknown && unknownState !== 'refused';

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(
    () => ({
      total: devices.length,
      active: devices.filter((d) => d.status === 'active').length,
      fleet: devices.filter((d) => d.vehicle_class === 'truck').length,
      loaders: devices.filter((d) => d.vehicle_class === 'loader').length,
      unknown: unknownDevices.length,
    }),
    [devices, unknownDevices]
  );

  // --- Register handlers ---
  const handleRegisterSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRegisterError(null);
    const imei = registerForm.imei.trim();
    if (!IMEI_RE.test(imei)) {
      setRegisterError('A valid numeric IMEI (8-17 digits) is required.');
      return;
    }
    setRegistering(true);
    try {
      const body: RegisterTelemetryDeviceBody = {
        imei,
        label: registerForm.label.trim() || null,
        model: registerForm.model.trim() || null,
        vehicleClass: registerForm.vehicleClass,
        vehicleRegNo: registerForm.vehicleRegNo.trim() || null,
      };
      await api.telemetry.registerDevice({ ...body });
      setRegisterForm(EMPTY_REGISTER_FORM);
      await fetchData();
    } catch (err) {
      setRegisterError(errorText(err));
    } finally {
      setRegistering(false);
    }
  };

  // Pre-fill the register form from an unknown device row
  const handlePrefillFromUnknown = (imei: string) => {
    setRegisterForm((prev) => ({ ...prev, imei }));
    setRegisterError(null);
    registerCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => imeiInputRef.current?.focus(), 350);
  };

  // --- Render ---
  return (
    <div className="py-6 max-w-[1400px]">
      <style>{pageStyles}</style>

      {/* ── Header Zone ── */}
      <div
        className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 dark:from-slate-100 dark:via-slate-100 dark:to-slate-50 p-8"
        style={{ animation: 'slideUp 0.4s ease-out' }}
      >
        {/* Breadcrumb */}
        <div className="relative flex items-center gap-1.5 text-sm mb-5">
          <button
            onClick={() => navigate('/settings')}
            className="text-slate-400 hover:text-white transition-colors font-medium"
          >
            Settings
          </button>
          <ChevronRight className="w-3 h-3 text-slate-600" />
          <span className="text-slate-500">LexiStar</span>
          <ChevronRight className="w-3 h-3 text-slate-600" />
          <span className="text-white font-semibold">LexiStar Devices</span>
        </div>

        {/* Title + Back */}
        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/settings')}
              className="p-2.5 text-slate-400 hover:text-white hover:bg-surface/10 rounded-xl transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">LexiStar Devices</h1>
              <p className="text-sm text-slate-400 mt-1">
                Satellite Tracking And Reporting — GPS device registry for Fleet vehicles and
                Loaders
              </p>
            </div>
          </div>
        </div>

        {/* Stats Strip */}
        <div
          className={`relative grid grid-cols-2 ${showUnknown && unknownState === 'shown' ? 'md:grid-cols-3 lg:grid-cols-5' : 'md:grid-cols-4'} gap-3 mt-7`}
        >
          {[
            {
              label: 'Devices',
              value: stats.total,
              icon: Satellite,
              color: 'from-slate-500/20 to-slate-600/20 border-slate-600/30',
            },
            {
              label: 'Active',
              value: stats.active,
              icon: CheckCircle2,
              color: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30',
            },
            {
              label: 'Fleet',
              value: stats.fleet,
              icon: Truck,
              color: 'from-sky-500/20 to-sky-600/20 border-sky-500/30',
            },
            {
              label: 'Loaders',
              value: stats.loaders,
              icon: Forklift,
              color: 'from-amber-500/20 to-amber-600/20 border-amber-500/30',
            },
            ...(showUnknown && unknownState === 'shown'
              ? [
                  {
                    label: 'Unknown',
                    value: stats.unknown,
                    icon: Radio,
                    color:
                      stats.unknown > 0
                        ? 'from-red-500/20 to-red-600/20 border-red-500/30'
                        : 'from-slate-500/20 to-slate-600/20 border-slate-600/30',
                  },
                ]
              : []),
          ].map((s, i) => (
            <div
              key={s.label}
              className={`bg-gradient-to-br ${s.color} border rounded-xl p-3.5 flex items-center gap-3 anim-stagger`}
              style={{ animationDelay: `${i * 0.05 + 0.15}s` }}
            >
              <s.icon className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <div>
                <div className="text-xl font-bold text-white tabular-nums leading-none">
                  {s.value}
                </div>
                <div className="text-[10px] text-slate-400 mt-1 font-semibold uppercase tracking-wider">
                  {s.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Register Device Form (manage permission only) ── */}
      {canManage && (
        <div
          ref={registerCardRef}
          className="bg-surface rounded-xl border border-slate-200/80 shadow-sm mb-6 anim-stagger"
          style={{ animationDelay: '0.25s' }}
        >
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
            <Plus className="w-4 h-4 text-teal-600" />
            <h2 className="text-sm font-bold text-slate-900">Register Device</h2>
          </div>
          <form onSubmit={handleRegisterSubmit} className="p-5">
            {registerError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                {registerError}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  IMEI <span className="text-red-400">*</span>
                </label>
                <input
                  ref={imeiInputRef}
                  type="text"
                  inputMode="numeric"
                  value={registerForm.imei}
                  onChange={(e) =>
                    setRegisterForm({ ...registerForm, imei: e.target.value.replace(/\D/g, '') })
                  }
                  className={`${inputClasses} font-mono tracking-wider`}
                  placeholder="e.g. 350317176700155"
                  maxLength={17}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Label</label>
                <input
                  type="text"
                  value={registerForm.label}
                  onChange={(e) => setRegisterForm({ ...registerForm, label: e.target.value })}
                  className={inputClasses}
                  placeholder="e.g. FMC003 test unit"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Model</label>
                <input
                  type="text"
                  value={registerForm.model}
                  onChange={(e) => setRegisterForm({ ...registerForm, model: e.target.value })}
                  className={inputClasses}
                  placeholder="e.g. Teltonika FMC003"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Class</label>
                <ThemedSelect
                  value={registerForm.vehicleClass}
                  onChange={(e) =>
                    setRegisterForm({ ...registerForm, vehicleClass: String(e.target.value) })
                  }
                  className={inputClasses}
                >
                  <option value="truck">Fleet vehicle (truck)</option>
                  <option value="loader">Loader</option>
                </ThemedSelect>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Vehicle Reg No
                </label>
                <input
                  type="text"
                  value={registerForm.vehicleRegNo}
                  onChange={(e) =>
                    setRegisterForm({ ...registerForm, vehicleRegNo: e.target.value })
                  }
                  className={inputClasses}
                  placeholder="e.g. ND 123-456"
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={registering}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {registering ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Registering...
                  </span>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    Register Device
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Devices Table ── */}
      <div
        className="bg-surface rounded-xl border border-slate-200/80 overflow-hidden shadow-sm anim-stagger"
        style={{ animationDelay: '0.3s' }}
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Satellite className="w-4 h-4 text-teal-600" />
            <h2 className="text-sm font-bold text-slate-900">Registered Devices</h2>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-50 border border-slate-200 rounded-lg transition-all disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-teal-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-500 mt-3">Loading...</p>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Satellite className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-medium text-slate-500">No devices registered yet</p>
            <p className="text-xs text-slate-400 mt-1">
              {canManage
                ? 'Register a GPS device above to start tracking it in LexiStar.'
                : 'GPS devices registered for your organization will appear here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    IMEI
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Label
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Model
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Class
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Reg No
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Last Seen
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Last Position
                  </th>
                  {canManage && (
                    <th className="text-right px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {devices.map((d, idx) => (
                  <tr
                    key={d.device_id}
                    className={`border-b border-slate-50 last:border-0 hover:bg-teal-50/30 transition-colors ${d.status === 'disabled' ? 'opacity-50' : ''}`}
                    style={{ animation: `slideDown 0.3s ease-out ${idx * 0.03}s both` }}
                  >
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-semibold text-slate-900 font-mono tracking-wider">
                        {d.imei}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      {d.label ? (
                        <span className="text-sm text-slate-700">{d.label}</span>
                      ) : (
                        <span className="text-xs text-slate-300 italic">No label</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {d.model ? (
                        <span className="text-sm text-slate-600">{d.model}</span>
                      ) : (
                        <span className="text-xs text-slate-300 italic">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <ClassBadge vehicleClass={d.vehicle_class} />
                    </td>
                    <td className="px-4 py-3.5">
                      {d.vehicle_reg_no ? (
                        <span className="text-sm font-semibold text-slate-700">
                          {d.vehicle_reg_no}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-300 italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={d.status} />
                    </td>
                    <td className="px-4 py-3.5">
                      <TimeCell value={d.last_seen_at} />
                    </td>
                    <td className="px-4 py-3.5">
                      <TimeCell value={d.last_position_at} />
                    </td>
                    {canManage && (
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditModal({ open: true, device: d })}
                            className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                            title="Edit"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-400 font-medium">
                {devices.length} device{devices.length !== 1 ? 's' : ''} registered
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Unknown Devices (global administrators with the manage permission, ST-00258) ── */}
      {showUnknown && !loading && (
        <div
          className="bg-surface rounded-xl border border-slate-200/80 overflow-hidden shadow-sm mt-6 anim-stagger"
          style={{ animationDelay: '0.35s' }}
        >
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2.5">
            <Radio
              className={`w-4 h-4 ${unknownDevices.length > 0 ? 'text-red-500' : 'text-slate-400'}`}
            />
            <h2 className="text-sm font-bold text-slate-900">Unknown Devices</h2>
            {unknownDevices.length > 0 && (
              <span className="text-xs tabular-nums font-bold px-1.5 py-0.5 rounded-md bg-red-50 text-red-600">
                {unknownDevices.length}
              </span>
            )}
          </div>
          {unknownState === 'failed' ? (
            <div className="px-5 py-6 text-sm text-red-700 flex items-start gap-2" role="alert">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              The unknown devices could not be loaded. The registered devices above are up to date;
              refresh the page to try again.
            </div>
          ) : unknownDevices.length === 0 ? (
            <div className="text-center py-12 px-6">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-emerald-400" />
              <p className="text-sm font-medium text-slate-500">No unknown devices knocking</p>
              <p className="text-xs text-slate-400 mt-1">
                Every IMEI that has contacted the ingest endpoint is registered.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      IMEI
                    </th>
                    <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      First Seen
                    </th>
                    <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Last Seen
                    </th>
                    <th className="text-center px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Attempts
                    </th>
                    <th className="text-right px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {unknownDevices.map((u, idx) => (
                    <tr
                      key={u.imei}
                      className="border-b border-slate-50 last:border-0 hover:bg-red-50/20 transition-colors"
                      style={{ animation: `slideDown 0.3s ease-out ${idx * 0.02}s both` }}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <Ban className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-semibold text-slate-900 font-mono tracking-wider">
                              {u.imei}
                            </span>
                            {u.remote_ip && (
                              <div className="text-[11px] text-slate-400 mt-0.5">
                                from {u.remote_ip}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <TimeCell value={u.first_seen_at} />
                      </td>
                      <td className="px-4 py-3.5">
                        <TimeCell value={u.last_seen_at} />
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-sm font-semibold text-slate-700 tabular-nums">
                          {u.attempt_count}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <button
                          onClick={() => handlePrefillFromUnknown(u.imei)}
                          className="px-3.5 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200/60 rounded-lg hover:bg-teal-100 transition-all"
                        >
                          Register
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-400 font-medium">
                  {unknownDevices.length} unregistered IMEI{unknownDevices.length !== 1 ? 's' : ''}{' '}
                  knocking on the ingest endpoint
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}
      <EditDeviceModal
        isOpen={editModal.open}
        device={editModal.device}
        onClose={() => setEditModal({ open: false, device: null })}
        onSaved={fetchData}
      />
    </div>
  );
}
